const SONG_TITLE_RE = /第\d+课\s+(.+)/;

/**
 * Extracts the English song name from a `第N课 <name>` title, e.g.
 * "第1课 Happy Birthday" → "Happy Birthday". Returns the input unchanged
 * if it doesn't match the pattern.
 */
export function extractSongTitle(title) {
  if (!title) return '';
  const m = title.match(SONG_TITLE_RE);
  return m ? m[1] : title;
}

/**
 * Extracts the phonics letter (e.g., "Aa", "Bb") from a lesson title.
 * Returns null if not a phonics b1 lesson or if extraction fails.
 */
export const getPhonicsLessonLetter = (topic, batch, lesson_number, phonicsLessons) => {
  if (topic?.toLowerCase() !== 'phonics' || batch?.toLowerCase() !== 'b1') return null;

  const idx = Number(lesson_number) - 1;
  if (!Number.isFinite(idx) || idx < 0 || idx >= phonicsLessons.length) return null;

  const title = phonicsLessons[idx].title;
  const match = title.match(/字母(.+)$/);
  return match ? match[1] : null;
};

/**
 * Gets a formatted lesson title, using phonics letter extraction for phonics b1.
 * Falls back to "topic batch/lesson_number" format.
 */
export const getLessonTitle = (topic, batch, lesson_number, phonicsLessons) => {
  const letter = getPhonicsLessonLetter(topic, batch, lesson_number, phonicsLessons);
  return letter ?? `${topic} ${batch}/${lesson_number}`;
};

/**
 * Pre-built lookup cache for lesson labels
 * Keys: "phonics|b1|1" -> "Aa", "songs|b1|1" -> "Happy Birthday"
 */
let lessonLabelCache = null;

/**
 * Build/get the lesson label cache.
 * Loads phonics and songs lessons once and creates a lookup map.
 * @returns {Promise<Object>} Cache object with keys like "phonics|b1|1" -> "Aa"
 */
export async function getLessonLabelCache() {
  if (lessonLabelCache) return lessonLabelCache;

  const { getLessonsByCourse } = await import('../data/lessonsRegistry');
  lessonLabelCache = {};

  // Load phonics lessons (two-pass: first collect patterns, then add IPA only to duplicates)
  const phonicsLessons = await getLessonsByCourse('phonics');

  // First pass: collect all patterns and their IPA
  const phonicsData = [];
  const patternCounts = {};

  phonicsLessons.forEach(lesson => {
    const lessonNum = parseInt(lesson.lessonNumber, 10);
    const title = lesson.title || '';
    const key = `phonics|${lesson.book}|${lessonNum}`;

    // B1 format: "第1课 字母Aa" - extract "Aa"
    const letterMatch = title.match(/字母([A-Za-z]+)/);
    if (letterMatch) {
      phonicsData.push({ key, label: letterMatch[1], ipa: null });
      return;
    }

    // B2+ format: "第27课 ad" or "第53课 a_e" or "第56课 -y" - extract word pattern after lesson number
    const wordMatch = title.match(/第\d+课\s+([-a-z_]+)/i);
    if (wordMatch) {
      const pattern = wordMatch[1];

      // Check for IPA in l2 of letters section
      let ipa = null;
      const letters = lesson.pages?.[1]?.sections?.letters;
      if (letters) {
        const l2 = letters.find(l => l.id === 'l2');
        if (l2?.text?.startsWith('/')) {
          ipa = l2.text;
        }
      }

      phonicsData.push({ key, label: pattern, ipa });
      patternCounts[pattern] = (patternCounts[pattern] || 0) + 1;
    }
  });

  // Second pass: add to cache, only including IPA for duplicate patterns
  phonicsData.forEach(({ key, label, ipa }) => {
    if (ipa && patternCounts[label] > 1) {
      lessonLabelCache[key] = `${label} ${ipa}`;
    } else {
      lessonLabelCache[key] = label;
    }
  });

  // Load songs lessons
  const songsLessons = await getLessonsByCourse('songs');
  songsLessons.forEach(lesson => {
    const lessonNum = parseInt(lesson.lessonNumber, 10);
    const title = lesson.title || '';

    // Songs format: "第1课 Happy Birthday" - extract song name
    const songMatch = title.match(/第\d+课\s+(.+)/);
    if (songMatch) {
      lessonLabelCache[`songs|${lesson.book}|${lessonNum}`] = songMatch[1];
    }
  });

  return lessonLabelCache;
}

// Keep old function name for backwards compatibility
export async function getPhonicsLetterCache() {
  return getLessonLabelCache();
}

/**
 * Format lesson display label for assignments.
 * For phonics: returns the letter (e.g., "Aa") or word pattern (e.g., "ad")
 * For songs: returns the song name (e.g., "Happy Birthday")
 * For others: returns "第{number}课"
 *
 * @param {string} topic - Course topic (e.g., 'phonics', 'songs')
 * @param {string} batch - Batch identifier (e.g., 'b1')
 * @param {string|number} lessonNumber - Lesson number
 * @param {Object} labelCache - Pre-loaded cache from getLessonLabelCache()
 * @returns {string} Formatted lesson label
 */
export function formatLessonLabel(topic, batch, lessonNumber, labelCache = null) {
  if (labelCache) {
    // Try topic-specific lookup
    const label = labelCache[`${topic}|${batch}|${lessonNumber}`];
    if (label) return label;

    // For phonics, also try legacy key format (backwards compat)
    if (topic === 'phonics') { // Keep hardcoded — this is a legacy data format check, not course behavior
      const legacyLabel = labelCache[`${batch}|${lessonNumber}`];
      if (legacyLabel) return legacyLabel;
    }
  }
  return `第${parseInt(lessonNumber, 10)}课`;
}
